<script setup lang="ts" generic="K">
import { ElTable, ElPagination, type ElCol, type Sort } from "element-plus";
import { ref, useSlots } from "vue";

export interface TableData<T> {
  prop: string;
  label: string;
  sortable?: string;
  columnKey?: string;
  formatter?: (row: T, col: object, value: object, index: number) => string;
  minWidth?: number | string;
}

export type SortChangedEvent = {
  prop: string;
  column: typeof ElCol;
  order: "ascending" | "descending" | null;
};

export interface Props<K> {
  data?: K[];
  isLoading: boolean;
  selectedObjects?: K[];
  pageNumber: number;
  numberOfPages: number;
  showHeader?: boolean;
  defaultSort?: Sort;
  rowClassName?: string | ((data: { row: K; rowIndex: number }) => string);
  cellClassName?:
    | string
    | ((data: { row: K; rowIndex: number; columnIndex: number }) => string);
  highlightCurrentRow?: boolean;
  showPagination?: boolean;
}

withDefaults(defineProps<Props<K>>(), {
  showHeader: true,
  highlightCurrentRow: true,
  showPagination: true,
});

const tableRef = ref();

const emit = defineEmits<{
  (event: "sort-change", payload: SortChangedEvent): void;
  (event: "update:selected-objects", selectedObjects: K[]): void;
  (event: "update:pageNumber", pageNumber: number): void;
  (event: "row-click", row: K): void;
}>();

const handleSelectionChange = (val: K[]) => {
  emit("update:selected-objects", val);
};

const handlePageChange = (newPage: number) => {
  emit("update:pageNumber", newPage);
};

const handleRowClick = (row: K) => {
  emit("row-click", row);
};

const handleSortChange = (event: SortChangedEvent) => {
  emit("sort-change", event);
};

const slots = useSlots();

const setCurrentRow = (row?: object) => {
  tableRef.value?.setCurrentRow(row);
};

const clearSelection = () => {
  tableRef.value?.clearSelection();
};

defineExpose({ setCurrentRow, clearSelection });
</script>

<template>
  <div
    v-if="slots['actions-left'] || slots['actions-right']"
    class="actions hide-on-print"
  >
    <slot name="actions-left" />
    <div class="flex-grow" />
    <slot name="actions-right" />
  </div>
  <el-table
    ref="tableRef"
    v-loading="isLoading"
    v-cy="'dataTable'"
    :data="data"
    :class="{ empty: !data?.length }"
    :row-class-name="rowClassName"
    :cell-class-name="cellClassName"
    table-layout="fixed"
    :highlight-current-row="highlightCurrentRow"
    :show-header="showHeader"
    :default-sort="defaultSort"
    @selection-change="handleSelectionChange"
    @row-click="handleRowClick"
    @sort-change="handleSortChange"
  >
    <slot name="columns" />
  </el-table>

  <el-pagination
    v-if="showPagination"
    layout="prev, pager, next"
    hide-on-single-page
    :page-count="numberOfPages"
    :current-page="pageNumber"
    @update:current-page="handlePageChange"
  />

  <slot
    v-if="!data?.length && !isLoading"
    name="empty-state"
  />
</template>

<style lang="scss" scoped>
.actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 25px;
  flex-wrap: wrap;

  @media screen and (max-width: $breakpoint-xs) {
    justify-content: space-between;
    padding-top: 0;
    margin-bottom: 20px;
  }
}

:deep(.date--cell.so-table__cell) {
  padding: 0 10px;

  .cell {
    padding: 0;
  }
}

:deep(.date-resource-column.so-table__cell) .cell {
  justify-content: center;
}

:deep(th.custom-field-column.so-table__cell) .cell {
  overflow-wrap: anywhere;
}

.so-table {
  --so-table-row-hover-bg-color: var(--so-bg-current-row-hover);
}

:deep(.so-table__row) {
  cursor: pointer;
}

.flex-grow {
  flex-grow: 1;
  margin-top: -10px;
  margin-left: -10px;
}

@media print {
  :deep(.so-scrollbar__view) {
    display: block !important;
  }

  :deep(.so-scrollbar) {
    height: auto;
  }

  :deep(.so-table__row) {
    break-inside: avoid;
    break-after: auto;
  }
}

:deep(.space-resource-count.so-table__cell .btn-resource-count) {
  font-weight: var(--font-normal);
}
</style>
